/**
 * ImageGalleryPicker - Reusable component for selecting images from user's gallery
 * Can be used anywhere in the app where image upload is needed
 */
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { imageStudioAPI, getAssetUrl } from "../lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Loader2, Image, Check, FolderOpen, RefreshCw } from "lucide-react";

export function ImageGalleryPicker({ 
  open, 
  onOpenChange, 
  onSelect,
  title = "Select from Gallery",
  description = "Choose an image from your previously created images"
}) {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchImages = async () => {
    setLoading(true);
    try {
      const response = await imageStudioAPI.getGallery();
      setImages(response || []);
    } catch (error) {
      console.error("Failed to fetch gallery:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchImages();
    }
  }, [open]);

  const handleSelect = async (image) => {
    try {
      const imageUrl = getAssetUrl(image.url || image.image_url);
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const fileName = `gallery_${image.id || Date.now()}.${blob.type.split('/')[1] || 'png'}`;
      const file = new File([blob], fileName, { type: blob.type });
      
      onSelect({
        file,
        preview: imageUrl,
        url: imageUrl,
        name: fileName,
        id: image.id,
        fromGallery: true,
      });
      
      onOpenChange(false);
      toast.success("Image selected");
    } catch (error) {
      console.error("Failed to load image:", error);
      toast.error("Failed to load image");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] bg-[#0A0A0B] border-border/50">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <FolderOpen className="w-5 h-5 text-[#00F0FF]" />
              {title}
            </DialogTitle>
            <Button variant="ghost" size="sm" onClick={fetchImages} disabled={loading}>
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        
        <div className="overflow-y-auto max-h-[55vh] pr-2">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-[#00F0FF] animate-spin" />
            </div>
          ) : images.length > 0 ? (
            <motion.div 
              className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              {images.map((img) => (
                <motion.div
                  key={img.id}
                  className="relative group rounded-lg overflow-hidden bg-black aspect-square cursor-pointer border-2 border-transparent hover:border-[#00F0FF]/50 transition-all"
                  onClick={() => handleSelect(img)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <img
                    src={getAssetUrl(img.url || img.image_url)}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Check className="w-6 h-6 text-[#00F0FF]" />
                  </div>
                  {img.type && (
                    <Badge className="absolute top-1 left-1 text-[10px] bg-black/70 text-white">
                      {img.type}
                    </Badge>
                  )}
                </motion.div>
              ))}
            </motion.div>
          ) : (
            <div className="text-center py-12">
              <Image className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No images in gallery</p>
              <p className="text-sm text-muted-foreground">Generate some in Image Studio first!</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function PickFromGalleryButton({ onSelect, className = "", size = "default" }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        variant="outline"
        size={size}
        className={`border-[#00F0FF]/30 hover:border-[#00F0FF]/50 hover:bg-[#00F0FF]/5 ${className}`}
        onClick={() => setOpen(true)}
        data-testid="pick-from-gallery-btn"
      >
        <FolderOpen className="w-4 h-4 mr-2 text-[#00F0FF]" />
        Pick from Gallery
      </Button>
      <ImageGalleryPicker open={open} onOpenChange={setOpen} onSelect={onSelect} />
    </>
  );
}

export default ImageGalleryPicker;
